/**
 * TOSS Web Application - SearchInput
 *
 * Compact search field with a leading magnifier icon and a trailing clear (×)
 * button. Used across directory list pages.
 *
 * **Высота — из `--h-control`, той же переменной, что у кнопки** (27.08.2026): 44 px на телефоне,
 * 40 на компьютере. Здесь стояло `h-9` с комментарием «matches Button size sm (h-9 / 36px)», и
 * ошибались обе половины: числовые классы считаются от плавающего корневого кегля, так что это
 * поле было 39.6 px на компьютере и 34.7 на телефоне — ни разу не 36. Пока по соседству стояла
 * кнопка того же `h-9`, ряд держался; стоило поставить рядом поле в пикселях или кнопку `md` —
 * разъезжался. Теперь разъезжаться нечему.
 */

import { forwardRef, type KeyboardEvent } from 'react';
import { Search, X } from 'lucide-react';
import { Spinner } from './Spinner';

export interface SearchInputProps {
  /** Current search text. */
  value: string;
  /** Called with the new text (already unwrapped from the event). */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Width / extra classes for the wrapper (e.g. "w-64"). */
  className?: string;
  /**
   * A request is in flight for what is currently typed.
   *
   * Replaces the magnifier rather than adding a control: a spinner that appears next to the field
   * moves whatever stands beside it, and it does so on every debounced keystroke — while somebody
   * is still typing. In the magnifier's place it says the same thing and shifts nothing.
   */
  isPending?: boolean;
  'aria-label'?: string;
  /**
   * Клавиши уходят наружу, когда полем поиска УПРАВЛЯЮТ спискoм рядом.
   *
   * Понадобилось выпадающему списку с поиском (`Select searchable`, 31.08.2026): строка стоит над
   * списком, но стрелки и Enter принадлежат списку, а не ей. Списочные страницы, где поиск сам по
   * себе, ничего не передают и ничего не замечают.
   */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Список, которым правит эта строка. */
  'aria-controls'?: string;
  /** Текущая строка того списка. */
  'aria-activedescendant'?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput({
  value,
  onChange,
  placeholder = 'Поиск...',
  className = '',
  isPending = false,
  'aria-label': ariaLabel = 'Поиск',
  onKeyDown,
  'aria-controls': ariaControls,
  'aria-activedescendant': ariaActiveDescendant,
}: SearchInputProps, ref): JSX.Element {
  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-disabled)]">
        {isPending ? <Spinner size="xs" /> : <Search className="w-4 h-4" />}
      </span>
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        // Мобильная клавиатура. `enterKeyHint` меняет подпись на клавише ввода с «Ввод» на
        // «Поиск» — единственный способ сказать заказчику, что нажатие здесь ищет, а не
        // отправляет форму. Остальные три гасят помощь, которая полю поиска только мешает:
        // номер контейнера `ABCD1234567` iOS охотно исправляет на слово, а первую букву
        // запроса — делает заглавной.
        enterKeyHint="search"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        aria-label={ariaLabel}
        aria-controls={ariaControls}
        aria-activedescendant={ariaActiveDescendant}
        className="
          w-full h-[var(--h-control)] pl-9 pr-8 rounded-md
          /* 16 px на телефоне, 14 шире — то же правило, что у `inputSizeStyles`
             (решение владельца 26.08.2026). Ниже 16 iOS Safari масштабирует страницу при
             фокусе, а это поле на телефоне трогают чаще любого другого. */
          text-touch md:text-md
          border border-[var(--color-border)] bg-[var(--color-bg-primary)]
          text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)]
          transition-colors
          focus:outline-none focus:border-[var(--color-accent-500)]
          focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-accent-500)_15%,transparent)]
        "
      />
      {value && (
        <button
          type="button"
          aria-label="Очистить поиск"
          onClick={() => onChange('')}
          className="
            absolute right-1.5 top-1/2 -translate-y-1/2
            inline-flex items-center justify-center w-6 h-6 rounded
            text-[var(--color-text-disabled)] hover:text-[var(--color-text-primary)]
            hover:bg-[var(--color-bg-secondary)] transition-colors
          "
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
});

export default SearchInput;
